import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import { HomePage } from './pages/HomePage';

// The legal page is the largest file in the project and is rarely visited, so
// it is split out of the initial bundle and fetched on demand.
const LegalPage = lazy(() => import('./pages/LegalPage').then(m => ({
  default: m.LegalPage
})));

// The calculator page pulls in a third-party embed, so it is also split out and
// only fetched when a visitor actually opens it.
const CalculatorPage = lazy(() => import('./pages/CalculatorPage').then(m => ({
  default: m.CalculatorPage
})));

// The savings roadmap is a self-contained tool with its own PDF library, so it
// is split out and only fetched when a visitor actually opens it.
const RoadmapPage = lazy(() => import('./pages/RoadmapPage').then(m => ({
  default: m.RoadmapPage
})));

// ScrollToAnchor component to handle hash links when navigating between pages
function ScrollToAnchor() {
  const {
    hash
  } = useLocation();
  useEffect(() => {
    if (hash) {
      setTimeout(() => {
        const element = document.getElementById(hash.replace('#', ''));
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [hash]);
  return null;
}
function RouteFallback() {
  return <div className="min-h-screen" aria-hidden="true" data-id="element-0" data-mp="33nq5xsl7735o" />;
}
function AppLayout({
  isDark,
  toggleTheme
}: {
  isDark: boolean;
  toggleTheme: () => void;
}) {
  return <div className="min-h-screen bg-[#f8f9fb] dark:bg-[#0f1629] text-slate-800 dark:text-slate-300 font-sans selection:bg-primary-500/30 transition-colors duration-300" data-id="element-1" data-mp="3dkl42adk71jf">
      <Navbar isDark={isDark} toggleTheme={toggleTheme} data-id="element-2" data-mp="nxn71wrpjbph" />
      <Suspense fallback={<RouteFallback data-id="element-4" data-mp="st2gmciomlg3" />} data-id="element-3" data-mp="stkne32z0i29">
        <Routes data-id="element-5" data-mp="swgkxqawp4q6">
          <Route path="/" element={<HomePage isDark={isDark} data-id="element-7" data-mp="35ox2qy1z4t4" />} data-id="element-6" data-mp="36hzjfpzb85g" />
          <Route path="/calculator" element={<CalculatorPage data-id="element-9" data-mp="3ud1glcmeruc7" />} data-id="element-8" data-mp="34nd1wz277z9" />
          <Route path="/roadmap" element={<RoadmapPage data-id="element-11" data-mp="3ucje3y95kjvu" />} data-id="element-10" data-mp="3ubrhck3tkg3z" />
          <Route path="/legal" element={<LegalPage data-id="element-13" data-mp="d4xq9hl3gfef" />} data-id="element-12" data-mp="d4nuteha5g1c" />
        </Routes>
      </Suspense>
      <Footer isDark={isDark} data-id="element-14" data-mp="d2bhxfqdpvv5" />
    </div>;
}
export function App() {
  // The theme is resolved in index.tsx before the first paint, so we read the
  // class that is already on the document rather than re-deriving it here.
  // Deriving it in React is what previously caused the white flash.
  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains('dark'));
  // Release the first-frame transition freeze once React is mounted, so theme
  // toggles still animate but the initial render never does.
  useEffect(() => {
    const id = window.requestAnimationFrame(() => {
      document.documentElement.classList.remove('no-transitions');
    });
    return () => window.cancelAnimationFrame(id);
  }, []);
  useEffect(() => {
    try {
      localStorage.setItem('sf-theme', isDark ? 'dark' : 'light');
    } catch {
      // Persisting the preference is best-effort only.
    }
    document.documentElement.classList.toggle('dark', isDark);
  }, [isDark]);
  const toggleTheme = () => setIsDark(prev => !prev);
  return <Router data-id="element-15" data-mp="13f7upnrlnphr">
      <ScrollToAnchor data-id="element-16" data-mp="13ey3jwutjcah" />
      <div className={`${isDark ? 'dark' : ''}`} data-id="element-17" data-mp="1r4987pfahqtw">
        <AppLayout isDark={isDark} toggleTheme={toggleTheme} data-id="element-18" data-mp="1r5katlx00sjf" />
      </div>
    </Router>;
}